import React from "react";
import { BsCheck } from "react-icons/bs";
import type { FeedbackOption } from "@pages/MissionCenter/Feedback";

export interface FeedbackQuestionBlockProps {
  questionId: number;
  questionBlockKey: number;
  question: string;
  questionType: 'single' | 'multiple';
  options: FeedbackOption[];
  selectedOptionIds: number[] | null;
  onSelectOption: (questionId: number, optionId: number[]) => void;
}

const FeedbackQuestionBlock: React.FC<FeedbackQuestionBlockProps> = ({
  questionId,
  questionBlockKey,
  question,
  questionType,
  options,
  selectedOptionIds,
  onSelectOption,
}) => {
  const handleOptionChange = (optionId: number) => {
    if (questionType === 'single') {
      onSelectOption(questionId, [optionId]);
      return;
    }
    const current = selectedOptionIds ?? [];
    const next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];
    onSelectOption(questionId, next);
  };
  return (
    <div
      className="card mt-3 w-full rounded-lg bg-base-100 first:mt-0"
      id={`feedback-question-${questionBlockKey}`}
    >
      <div className="card-body p-3 sm:p-4">
        <div className="text-left text-base font-semibold text-(--dark-gray)">
          {question}
          <span className="text-[#F5A623]" aria-hidden>
            {" "}
            *
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {options.map((option) => {
            const isSelected = selectedOptionIds?.includes(option.twFeedbackOptionId) ?? false;
            const inputId = `fb-${questionId}-${option.twFeedbackOptionId}`;
            const isSingle = questionType === 'single';
            return (
              <label
                key={option.twFeedbackOptionId}
                htmlFor={inputId}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2.5 transition-colors ${
                  isSelected
                    ? "border-(--button-green) bg-[#E8F5E9]"
                    : "border-(--gray-color) bg-[#F8F7F5] hover:bg-gray-100"
                }`}
              >
                <div className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                  <input
                    id={inputId}
                    type={isSingle ? 'radio' : 'checkbox'}
                    name={`feedback-q-${questionId}`}
                    className="peer absolute h-full w-full cursor-pointer opacity-0"
                    checked={isSelected}
                    onChange={() => handleOptionChange(option.twFeedbackOptionId)}
                  />
                  {isSingle ? (
                    <div
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all duration-200 ${
                        isSelected
                          ? "border-(--button-green) bg-(--button-green)"
                          : "border-(--primary-green) bg-white"
                      }`}
                    >
                      {isSelected ? <span className="h-2 w-2 rounded-full bg-white" /> : null}
                    </div>
                  ) : (
                    <div
                      className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all duration-200 ${
                        isSelected
                          ? "border-(--button-green) bg-(--button-green)"
                          : "border-(--primary-green) bg-white"
                      }`}
                    >
                      {isSelected ? <BsCheck className="text-lg text-white" /> : null}
                    </div>
                  )}
                </div>
                <span className="text-left font-normal text-xs leading-snug text-(--dark-gray)">{option.twOption}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FeedbackQuestionBlock;
